<template>
  <div class="modal-card up-modal">
    <header class="modal-card-head up-modal-head">
      <div class="up-modal-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2h-4v-7H9v7H5a2 2 0 0 1-2-2V9z"></path>
        </svg>
      </div>
      <div>
        <h2 class="up-modal-title">Create Classroom</h2>
        <p class="up-modal-sub">Pick a grade-subject pair and a section to set up the classroom.</p>
      </div>
      <button class="up-modal-close" type="button" @click="$parent.close()" aria-label="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    </header>

    <section class="modal-card-body up-modal-body">
      <div class="up-grid">
        <div class="up-field">
          <label class="up-label">Grade &amp; Subject <span class="req">*</span></label>
          <div class="select-wrap">
            <select v-model="classroom.grade_subject">
              <option value="" disabled>Select grade &amp; subject</option>
              <option v-for="g in fetchGradeSubjects" :key="g._id" :value="g._id">
                {{ g.grade.name }} — {{ g.subject.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="up-field">
          <label class="up-label">Section <span class="req">*</span></label>
          <div class="select-wrap">
            <select v-model="classroom.section">
              <option value="" disabled>Select section</option>
              <option v-for="s in fetchSections" :key="s._id" :value="s._id">{{ s.name }}</option>
            </select>
          </div>
        </div>

        <div class="up-field full">
          <label class="up-label">Remarks</label>
          <input
            v-model="classroom.remarks"
            type="text"
            class="up-input"
            placeholder="Optional notes about this classroom"
          />
        </div>
      </div>
    </section>

    <footer class="modal-card-foot up-modal-foot">
      <button class="up-btn up-btn-ghost" type="button" @click="$parent.close()">Cancel</button>
      <button class="up-btn up-btn-primary" :disabled="isLoading || !canSubmit" @click="proceed()">
        <span v-if="!isLoading">Create Classroom</span>
        <span v-else>Creating…</span>
      </button>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'set-classroom',
  data() {
    return {
      isLoading: false,
      classroom: {
        grade_subject: '',
        section: '',
        remarks: ''
      }
    }
  },
  computed: {
    ...mapGetters(['getTeacherToken', 'fetchGradeSubjects', 'fetchSections']),
    canSubmit() {
      return !!(this.classroom.grade_subject && this.classroom.section)
    }
  },
  methods: {
    ...mapActions(['createClassroom', 'loadSections', 'loadGradeSubjects']),
    proceed() {
      if (!this.canSubmit) {
        this.$buefy.toast.open({ message: 'Please select grade-subject and section', type: 'is-warning' })
        return
      }
      this.isLoading = true
      this.createClassroom({
        classroom: this.classroom,
        token: this.getTeacherToken
      })
        .then(() => {
          this.isLoading = false
          this.$emit('over')
          this.$buefy.toast.open({ message: 'Classroom created successfully', type: 'is-success' })
        })
        .catch(err => {
          this.isLoading = false
          this.$buefy.toast.open({
            message: (err.response && err.response.data && err.response.data.msg) || 'Could not create classroom',
            type: 'is-danger'
          })
        })
    }
  },
  async created() {
    this.loadSections({ token: this.getTeacherToken })
    this.loadGradeSubjects({ token: this.getTeacherToken })
  }
}
</script>

<style scoped>
.up-modal.modal-card {
  width: min(560px, 95vw);
  max-width: 95vw;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.25);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 40px);
}

.up-modal-head.modal-card-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
  padding: 1.25rem 1.4rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, #fafbfd, #ffffff);
  position: relative;
  border-radius: 0;
}

.up-modal-icon {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: linear-gradient(135deg, #8919e7, #4361ee);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.15);
}

.up-modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  letter-spacing: -0.01em;
}

.up-modal-sub {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0.1rem 0 0;
}

.up-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.05);
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.up-modal-close:hover {
  background: #1f2937;
  color: #ffffff;
}

.up-modal-body.modal-card-body {
  padding: 1.25rem 1.4rem;
  background: #ffffff;
  flex: 1 1 auto;
  overflow-y: auto;
}

.up-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;
}
.up-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.up-field.full {
  grid-column: 1 / -1;
}

.up-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.01em;
}
.up-label .req {
  color: #ef4444;
  margin-left: 2px;
}

.up-input,
.select-wrap select {
  width: 100%;
  border: 1.5px solid #e5e7eb;
  background: #ffffff;
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
  font-size: 0.95rem;
  color: #1f2937;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.up-input:hover,
.select-wrap select:hover {
  border-color: #cbd5e1;
}

.up-input:focus,
.select-wrap select:focus {
  border-color: #8919e7;
  box-shadow: 0 0 0 3px rgba(137, 25, 231, 0.12);
}

.select-wrap {
  position: relative;
}
.select-wrap select {
  appearance: none;
  cursor: pointer;
  padding-right: 2rem;
}
.select-wrap::after {
  content: '';
  position: absolute;
  right: 0.85rem;
  top: 50%;
  width: 8px;
  height: 8px;
  border-right: 2px solid #94a3b8;
  border-bottom: 2px solid #94a3b8;
  transform: translateY(-65%) rotate(45deg);
  pointer-events: none;
}

.up-modal-foot.modal-card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  padding: 1rem 1.4rem 1.25rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fafbfd;
  border-radius: 0;
}

.up-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.65rem 1.1rem;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.up-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.up-btn-ghost {
  background: transparent;
  color: #475569;
  border: 1px solid rgba(15, 23, 42, 0.1);
}
.up-btn-ghost:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #1f2937;
}
.up-btn-primary {
  background: linear-gradient(135deg, #8919e7, #6b38c7);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(137, 25, 231, 0.28);
}
.up-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(137, 25, 231, 0.36);
}

@media screen and (max-width: 540px) {
  .up-grid {
    grid-template-columns: 1fr;
  }
  .up-modal-foot {
    flex-direction: column-reverse;
  }
  .up-btn {
    width: 100%;
  }
}
</style>
